/* global XLSX */
const PlummeProductImportExport = (() => {
  const kindToType = (kind) => kind === 'physical' ? 'Vare' : kind === 'subscription' ? 'Abonnement' : kind === 'rental' ? 'Utleie' : 'Tjeneste';
  const vatOptions = () => window.PlummeBackend?.getSalesVatOptions?.() || [25, 15, 12, 0].map((v) => ({ value: String(v), label: `${v} % MVA` }));
  const vatMeta = (value) => window.PlummeBackend?.normalizeSalesVatOption?.(value) || { rate: Number(value) || 0, treatment: Number(value) ? 'standard' : 'zeroRated' };
  const defaultSalesAccount = (vat, type = 'Vare') => {
    if (vat.treatment === 'exempt') return '3200';
    if (['salesReverseCharge51', 'export52', 'zeroRated'].includes(vat.treatment)) return '3100';
    if (vat.treatment === 'standard') {
      if (Number(vat.rate) === 15) return '3030';
      if (Number(vat.rate) === 12) return '3050';
      return type === 'Tjeneste' ? '3020' : '3000';
    }
    return type === 'Tjeneste' ? '3020' : '3000';
  };
  const vatSelection = (product) => product.vatSelection || window.PlummeBackend?.encodeSalesVatSelection?.(product.vatTreatment, product.vat) || String(product.vat ?? 25);
  const vatLabel = (product) => {
    const value = vatSelection(product);
    return vatOptions().find((option) => option.value === value)?.label || `${product.vat ?? 0} %`;
  };
  const rentalPricing = (product) => window.PlummeRentalPricing?.normalizeRentalPricing?.(product?.rentalPricing) || { enabled: false, dayRates: [] };
  const rentalPricingLabel = (product) => window.PlummeRentalPricing?.describeRentalPricing?.(rentalPricing(product)) || '';
  const toUiProduct = (product = {}) => {
    const type = product.type || kindToType(product.kind);
    return {
      ...product,
      nr: product.nr ?? product.itemNumber ?? '',
      name: product.name || '',
      desc: product.desc ?? product.description ?? '',
      price: product.price ?? product.unitPrice ?? 0,
      vat: product.vat ?? product.vatRate ?? 25,
      vatSelection: product.vatSelection || window.PlummeBackend?.encodeSalesVatSelection?.(product.vatTreatment, product.vatRate ?? product.vat) || String(product.vat ?? product.vatRate ?? 25),
      vatTreatment: product.vatTreatment || (Number(product.vat ?? product.vatRate ?? 25) === 0 ? 'zeroRated' : 'standard'),
      type,
      stock: product.stock ?? (type === 'Vare' || type === 'Utleie' ? (product.stockQuantity ?? 0) : '-'),
      period: product.period ?? (type === 'Abonnement' ? String(product.subscriptionPeriodMonths || 1) : undefined),
      salesAccountCode: product.salesAccountCode || '3000',
      salesAccountEquivalentCode: product.salesAccountEquivalentCode || product.yearClosingStandardAccountCode || '',
      status: product.status === 'archived' ? 'passiv' : (product.status || 'aktiv'),
    };
  };
  const productExportColumns = () => [
    { header: t('prd.itemNo'), value: (p) => toUiProduct(p).nr || '' },
    { header: t('prd.colProductName'), value: (p) => toUiProduct(p).name || '' },
    { header: t('prd.colDescription'), value: (p) => toUiProduct(p).desc || '' },
    { header: t('prd.priceExVat'), value: (p) => toUiProduct(p).price || 0 },
    { header: t('prd.colVat'), value: (p) => vatSelection(toUiProduct(p)) },
    { header: t('prd.colType'), value: (p) => toUiProduct(p).type || 'Tjeneste' },
    { header: t('prd.stock'), value: (p) => {
      const product = toUiProduct(p);
      return product.type === 'Vare' || product.type === 'Utleie' ? (product.stock || 0) : '';
    } },
    { header: t('prd.colRentalDiscount'), value: (p) => {
      const product = toUiProduct(p);
      return product.type === 'Utleie' ? rentalPricingLabel(product) : '';
    } },
    { header: t('prd.subscriptionPeriod'), value: (p) => {
      const product = toUiProduct(p);
      return product.type === 'Abonnement' ? (product.period || '1') : '';
    } },
    { header: t('prd.colSalesAccount'), value: (p) => toUiProduct(p).salesAccountCode || '3000' },
    { header: t('prd.colEquivalentAccount'), value: (p) => toUiProduct(p).salesAccountEquivalentCode || '' },
    { header: t('prd.colStatus'), value: (p) => toUiProduct(p).status || 'aktiv' },
  ];
  const csvCell = (value) => String(value ?? '').trim();
  const splitCsvLine = (line, delimiter) => {
    const cells = [];
    let current = '';
    let quoted = false;
    for (let i = 0; i < line.length; i += 1) {
      const ch = line[i];
      if (ch === '"' && quoted && line[i + 1] === '"') { current += '"'; i += 1; continue; }
      if (ch === '"') { quoted = !quoted; continue; }
      if (ch === delimiter && !quoted) { cells.push(current); current = ''; continue; }
      current += ch;
    }
    cells.push(current);
    return cells.map(csvCell);
  };
  const normalizeHeader = (header) => String(header || '').toLowerCase().replace(/\s|\.|-/g, '');
  const numberValue = (value, fallback = 0) => {
    const cleaned = String(value ?? '')
      .replace(/\u00A0/g, ' ')
      .replace(/kr|nok/gi, '')
      .replace(/[^\d,.-]/g, '')
      .trim();
    const decimalComma = cleaned.includes(',') && (!cleaned.includes('.') || cleaned.lastIndexOf(',') > cleaned.lastIndexOf('.'));
    const normalized = decimalComma
      ? cleaned.replace(/\./g, '').replace(',', '.')
      : cleaned.replace(/,/g, '');
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : fallback;
  };
  const vatInput = (value) => {
    const text = String(value ?? '').trim();
    const normalized = text.toLowerCase();
    if (['hoy', 'høy', 'high', 'standard', 'standardrate'].includes(normalized)) return '25';
    if (['middels', 'medium', 'middle', 'mid'].includes(normalized)) return '15';
    if (['lav', 'low', 'reduced'].includes(normalized)) return '12';
    if (['ingen', 'null', 'zero', 'zerorated', 'fritatt', 'unntatt'].includes(normalized)) return 'zeroRated';
    return text;
  };
  const parseMatrix = (matrix) => {
    if (!Array.isArray(matrix) || matrix.length < 2) return [];
    const headers = matrix[0].map(normalizeHeader);
    const pick = (row, names) => {
      const index = headers.findIndex((header) => names.includes(header));
      return index >= 0 ? row[index] || '' : '';
    };
    return matrix.slice(1).map((row) => {
      const typeText = String(pick(row, ['type', 'produkttype', 'kind']) || '').toLowerCase();
      const uiType = typeText.includes('tjeneste') || typeText.includes('service') ? 'Tjeneste' : typeText.includes('abonnement') || typeText.includes('subscription') ? 'Abonnement' : typeText.includes('utleie') || typeText.includes('rental') ? 'Utleie' : 'Vare';
      const selectedVat = vatInput(pick(row, ['mva', 'mvatype', 'mvasats', 'mvaprosent', 'vat', 'vattype', 'vatrate', 'vatselection', 'mvakode']) || '25');
      const vat = vatMeta(selectedVat);
      return {
        nr: pick(row, ['varenr', 'varenummer', 'produktnr', 'produktnummer', 'itemnumber', 'productnumber', 'itemno', 'sku', 'number', 'nummer', 'kode']),
        name: pick(row, ['produktnavn', 'produktbeskrivelse', 'produkt', 'navn', 'name', 'product', 'productname']),
        desc: pick(row, ['beskrivelse', 'description']),
        sku: pick(row, ['sku', 'varenr', 'varenummer', 'produktnr', 'produktnummer']),
        ean: pick(row, ['ean', 'gtin', 'barcode', 'strekkode']),
        gtin: pick(row, ['gtin', 'ean', 'barcode', 'strekkode']),
        productGroup: pick(row, ['produktgruppe', 'gruppe', 'productgroup', 'group']),
        category: pick(row, ['kategori', 'category', 'categories']),
        brand: pick(row, ['merke', 'brand']),
        variantName: pick(row, ['variant', 'variantnavn', 'variantname', 'variation']),
        supplierItemNumber: pick(row, ['leverandorvarenr', 'leverandørvarenr', 'supplieritemnumber', 'suppliernumber']),
        internalReference: pick(row, ['internreferanse', 'internalreference', 'reference']),
        tags: String(pick(row, ['tags', 'tagger', 'stikkord']) || '').split(/[;,]/).map((tag) => tag.trim()).filter(Boolean),
        price: numberValue(pick(row, ['priseksmva', 'priseks', 'enhetspris', 'unitprice', 'price', 'salgspris', 'nettopris'])),
        vat: vat.rate,
        vatSelection: selectedVat,
        vatTreatment: vat.treatment,
        type: uiType,
        stock: uiType === 'Vare' || uiType === 'Utleie' ? numberValue(pick(row, ['lager', 'varebeholdning', 'stock', 'stockquantity', 'tilgjengelig'])) : '-',
        period: uiType === 'Abonnement' ? String(numberValue(pick(row, ['abonnementsperiode', 'subscriptionperiod', 'period']), 1)) : undefined,
        salesAccountCode: pick(row, ['salgskonto', 'inntektskonto', 'account', 'salesaccount', 'salesaccountcode']) || defaultSalesAccount(vat, uiType),
        salesAccountEquivalentCode: pick(row, ['tilsvarerstandardkonto', 'standardkonto', 'equivalentaccount', 'equivalentcode', 'yearclosingstandardaccountcode']) || '',
      };
    }).filter((product) => product.name);
  };
  const parseCsv = (text) => {
    const clean = String(text || '').replace(/^\uFEFF/, '');
    const lines = clean.split(/\r?\n/).filter((line) => line.trim());
    if (lines.length < 2) return [];
    const delimiter = (lines[0].match(/;/g) || []).length >= (lines[0].match(/,/g) || []).length ? ';' : ',';
    return parseMatrix(lines.map((line) => splitCsvLine(line, delimiter)));
  };
  const parseXlsx = async (file) => {
    await loadXLSX().catch(() => {});
    if (!window.XLSX) throw new Error(t('prd.excelImportUnavailable'));
    const workbook = window.XLSX.read(await file.arrayBuffer(), { type: 'array' });
    const sheetName = workbook.SheetNames?.[0];
    if (!sheetName) return [];
    return parseMatrix(window.XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { header: 1, raw: false, defval: '' }));
  };
  const parseFile = async (file) => file.name.toLowerCase().endsWith('.xlsx') ? parseXlsx(file) : parseCsv(await file.text());
  const exportRowsXlsx = async (filename, rows, columns) => {
    await loadXLSX().catch(() => {});
    if (!window.XLSX) throw new Error(t('prd.excelExportUnavailable'));
    const matrix = [
      columns.map((column) => column.header),
      ...rows.map((row) => columns.map((column) => column.value(row))),
    ];
    const workbook = window.XLSX.utils.book_new();
    const worksheet = window.XLSX.utils.aoa_to_sheet(matrix);
    window.XLSX.utils.book_append_sheet(workbook, worksheet, t('prd.exportSheetName'));
    window.XLSX.writeFile(workbook, `${filename}.xlsx`);
  };
  const buildExportRows = (rows) => (rows || []).map(toUiProduct);
  const COUNT_TEMPLATE_HEADERS = window.PlummeInventoryCountSheet.headers;
  const exportCountTemplate = (filename, rows) => window.PlummeInventoryCountSheet.exportTemplate(filename, buildExportRows(rows));
  const exportRows = (filename, rows, kind = 'csv') => {
    const products = buildExportRows(rows);
    const columns = productExportColumns();
    if (kind === 'xlsx') return exportRowsXlsx(filename, products, columns);
    if (window.exportTable) return window.exportTable(filename, columns, products, 'csv');
    const SEP = ';';
    const esc = (value) => {
      const text = value == null ? '' : String(value);
      return /[";\n\r]/.test(text) ? '"' + text.replace(/"/g, '""') + '"' : text;
    };
    const lines = [
      columns.map((column) => esc(column.header)).join(SEP),
      ...products.map((product) => columns.map((column) => esc(column.value(product))).join(SEP)),
    ];
    const blob = new Blob(['\uFEFF' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  return { vatOptions, vatMeta, vatSelection, vatLabel, productExportColumns, toUiProduct, buildExportRows, parseFile, parseCsv, parseXlsx, exportRows, exportRowsXlsx, exportCountTemplate, COUNT_TEMPLATE_HEADERS };
})();

Object.assign(window, { PlummeProductImportExport });
